import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Alert,
  Platform,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { enqueueScoreJob } from '@/lib/scoreQueue';
import { Dumbbell } from 'lucide-react-native';
import { shareWorkout, type ShareableWorkout } from '@/lib/workoutShare';
import SkeletonRow from '@/components/SkeletonRow';
import WorkoutLogCard from '@/components/WorkoutLogCard';
import { useUnits } from '@/lib/useUnits';

const log = createLogger('WORKOUT_LOGS');

/** All workouts can be deleted — no time restriction on own workouts. */
function isWithinDeleteWindow(_performedAt: string | null, _createdAt: string | null): boolean {
  return true; // Users can always delete their own workouts
}

interface WorkoutExercise {
  exercise_id: string;
  name: string;
  sets: {
    reps: number;
    weight: number;
    is_pr: boolean;
    media_url?: string | null;
  }[];
  notes?: string | null;
  is_competition?: boolean;
}

interface WorkoutPayload {
  exercises: WorkoutExercise[];
}

interface Workout {
  id: string;
  user_id: string;
  gym_id: string | null;
  performed_at: string;
  payload: WorkoutPayload;
  created_at: string;
}

export default function WorkoutLogs() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const { displayWeight, userUnits } = useUnits();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedWorkouts, setExpandedWorkouts] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadWorkouts();

    // Subscribe to real-time workout updates
    const subscription = supabase
      .channel('workouts_channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'workouts' },
        () => {
          loadWorkouts();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadWorkouts = async () => {
    if (!profile?.id) return;

    log.info('Loading workouts for user:', profile.id);

    try {
      const { data, error } = await supabase
        .from('workouts')
        .select('*')
        .eq('user_id', profile.id)
        .order('performed_at', { ascending: false })
        .limit(30);

      if (error) {
        log.error('Error loading workouts:', error);
        throw error;
      }

      log.info(`Loaded ${data?.length || 0} workouts`);
      setWorkouts((data as Workout[]) || []);
    } catch (error: unknown) {
      log.error('Failed to load workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkout = (workoutId: string) => {
    const newExpanded = new Set(expandedWorkouts);
    if (newExpanded.has(workoutId)) {
      newExpanded.delete(workoutId);
    } else {
      newExpanded.add(workoutId);
    }
    setExpandedWorkouts(newExpanded);
  };

  const deleteWorkout = async (workoutId: string) => {
    const workout = workouts.find(w => w.id === workoutId);
    if (workout && !isWithinDeleteWindow(workout.performed_at, workout.created_at)) {
      const msg = 'Workouts can only be deleted within 24 hours of logging.';
      if (Platform.OS === 'web') {
        alert(msg);
      } else {
        Alert.alert('Cannot Delete', msg);
      }
      return;
    }

    if (Platform.OS === 'web') {
      if (!confirm('Delete this workout? This cannot be undone.')) return;
    } else {
      Alert.alert(
        'Delete Workout?',
        'This cannot be undone.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => await performDeleteWorkout(workoutId),
          },
        ]
      );
      return;
    }
    await performDeleteWorkout(workoutId);
  };

  const performDeleteWorkout = async (workoutId: string) => {
    try {
      const { error } = await supabase
        .from('workouts')
        .delete()
        .eq('id', workoutId)
        .eq('user_id', profile!.id);

      if (error) throw error;

      setWorkouts(workouts.filter(w => w.id !== workoutId));

      // [DEV DIAGNOSTIC] Track score before/after recompute
      if (profile?.id) {
        const scoreBefore = null;
        const timestamp = new Date().toISOString();

        log.info('[DIAGNOSTIC] Score Before Recompute:', {
          timestamp,
          trigger: 'workout_delete',
          userId: profile.id,
          scoreBefore,
        });

        await enqueueScoreJob(profile.id, 'workout_delete');

        const scoreAfter = null;

        log.info('[DIAGNOSTIC] Score After Recompute:', {
          timestamp,
          trigger: 'workout_delete',
          userId: profile.id,
          scoreBefore,
          scoreAfter,
          delta: null,
        });
      }
    } catch (error: unknown) {
      log.error('Delete workout error:', error);
      const msg = error instanceof Error ? error.message : (error as any)?.message ?? 'Unknown error';
      if (Platform.OS === 'web') {
        alert(`Failed to delete workout: ${msg}`);
      } else {
        Alert.alert('Delete Failed', `Couldn't delete that workout.\n\n${msg}`);
      }
    }
  };

  const deleteExercise = async (workoutId: string, exerciseIndex: number) => {
    const workout = workouts.find(w => w.id === workoutId);
    if (!workout) return;

    if (!isWithinDeleteWindow(workout.performed_at, workout.created_at)) {
      const msg = 'Exercises can only be removed within 24 hours of logging.';
      if (Platform.OS === 'web') {
        alert(msg);
      } else {
        Alert.alert('Cannot Delete', msg);
      }
      return;
    }

    const exerciseName = workout.payload.exercises[exerciseIndex]?.name || 'exercise';

    if (Platform.OS === 'web') {
      if (!confirm(`Delete ${exerciseName}?`)) return;
    } else {
      Alert.alert(
        'Delete Exercise?',
        `Remove ${exerciseName} from this workout?`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => await performDeleteExercise(workoutId, exerciseIndex),
          },
        ]
      );
      return;
    }
    await performDeleteExercise(workoutId, exerciseIndex);
  };

  const performDeleteExercise = async (workoutId: string, exerciseIndex: number) => {
    try {
      const workout = workouts.find(w => w.id === workoutId);
      if (!workout) return;

      const updatedExercises = workout.payload.exercises.filter((_, idx) => idx !== exerciseIndex);

      if (updatedExercises.length === 0) {
        if (Platform.OS === 'web') {
          if (confirm('Workout is now empty. Delete entire workout?')) {
            await performDeleteWorkout(workoutId);
            return;
          }
        } else {
          Alert.alert(
            'Workout Empty',
            'Delete entire workout or keep it?',
            [
              { text: 'Keep Empty', onPress: async () => await saveEmptyWorkout(workoutId) },
              {
                text: 'Delete Workout',
                style: 'destructive',
                onPress: async () => await performDeleteWorkout(workoutId),
              },
            ]
          );
          return;
        }
      }

      const { error } = await supabase
        .from('workouts')
        .update({
          payload: { exercises: updatedExercises }
        })
        .eq('id', workoutId);

      if (error) throw error;

      setWorkouts(workouts.map(w =>
        w.id === workoutId
          ? { ...w, payload: { exercises: updatedExercises } }
          : w
      ));

      // [DEV DIAGNOSTIC] Track score before/after recompute
      if (profile?.id) {
        const scoreBefore = null;
        const timestamp = new Date().toISOString();

        log.info('[DIAGNOSTIC] Score Before Recompute:', {
          timestamp,
          trigger: 'exercise_delete',
          userId: profile.id,
          scoreBefore,
        });

        await enqueueScoreJob(profile.id, 'manual');

        const scoreAfter = null;

        log.info('[DIAGNOSTIC] Score After Recompute:', {
          timestamp,
          trigger: 'exercise_delete',
          userId: profile.id,
          scoreBefore,
          scoreAfter,
          delta: null,
        });
      }
    } catch (error: unknown) {
      log.error('Delete exercise error:', error);
      if (Platform.OS === 'web') {
        alert('Failed to delete exercise');
      } else {
        Alert.alert('Oops', "Couldn't delete that exercise. Please try again.");
      }
    }
  };

  const saveEmptyWorkout = async (workoutId: string) => {
    await supabase
      .from('workouts')
      .update({ payload: { exercises: [] } })
      .eq('id', workoutId);

    setWorkouts(workouts.map(w =>
      w.id === workoutId ? { ...w, payload: { exercises: [] } } : w
    ));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else if (date.getFullYear() === today.getFullYear()) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    }
  };

  if (loading) {
    return (
      <View style={[styles.centerContainer, { backgroundColor: theme.colors.background, padding: 24 }]}>
        <SkeletonRow rows={5} />
      </View>
    );
  }

  if (workouts.length === 0) {
    return (
      <View style={[styles.centerContainer, { backgroundColor: theme.colors.background }]}>
        <Dumbbell size={48} color={theme.colors.textTertiary} />
        <View style={{ padding: 24, alignItems: 'center' }}>
          <Text style={{ color: theme.colors.textSecondary, fontSize: 16, fontWeight: '600', marginBottom: 8 }}>
            No workouts yet
          </Text>
          <Text style={{ color: theme.colors.textTertiary, fontSize: 14, textAlign: 'center', lineHeight: 20 }}>
            Tap the + button to log your first lift and start building your strength score.
          </Text>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      contentContainerStyle={styles.content}>
      {workouts.map((workout) => {
        const workoutPayload = typeof workout.payload === 'string' ? JSON.parse(workout.payload) : workout.payload;
        const workoutUnits = workoutPayload?.units || 'lbs';

        return (
          <WorkoutLogCard
            key={workout.id}
            workout={workout}
            isExpanded={expandedWorkouts.has(workout.id)}
            theme={theme}
            userUnits={userUnits}
            displayWeight={displayWeight}
            formatDate={formatDate}
            onToggle={() => toggleWorkout(workout.id)}
            onDelete={() => deleteWorkout(workout.id)}
            onDeleteExercise={(idx) => deleteExercise(workout.id, idx)}
            onShare={() => {
              let totalVolume = 0;
              workout.payload.exercises?.forEach((exercise) => {
                exercise.sets.forEach((set) => {
                  totalVolume += displayWeight(set.weight, workoutUnits) * set.reps;
                });
              });
              const exercises = (workout.payload.exercises || []).map((ex) => {
                const best = ex.sets.reduce((a, s) => (s.weight > a.weight ? s : a), ex.sets[0]);
                const convertedWeight = best ? displayWeight(best.weight, workoutUnits) : 0;
                return {
                  name: ex.name,
                  topSet: best ? `${convertedWeight}×${best.reps}` : '',
                  isPR: ex.sets.some((s) => s.is_pr),
                };
              });
              const sw: ShareableWorkout = {
                exercises,
                totalVolume,
                unit: userUnits,
                date: workout.performed_at,
                handle: profile?.handle || profile?.display_name || '',
                strengthScore: 0,
              };
              shareWorkout(sw);
            }}
          />
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '700',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
  },

});
